// Phase 6C · Reusable AI Studio Composer.
// Mountable from Invoice / Lead / Project detail surfaces by passing
// `sourceEntityType` + `sourceEntityId`. Standalone (manual) usage on
// the dedicated Drafts page.
//
// Additive. No locked component modified.
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { composeDraft } from "@/lib/aiStudioDrafts";

export default function AIStudioComposer({
  sourceEntityType = "manual",
  sourceEntityId = null,
  defaultTitle = "",
  defaultPrompt = "",
  defaultKind = "text",
  triggerLabel = "AI Studio",
  triggerVariant = "default",
  onCreated,
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(defaultTitle);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [kind, setKind] = useState(defaultKind);
  const [useBrand, setUseBrand] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim() || !prompt.trim()) {
      return toast.error("Title and prompt are required");
    }
    setBusy(true);
    try {
      const d = await composeDraft({
        title, kind, prompt,
        source_entity_type: sourceEntityType,
        source_entity_id: sourceEntityId,
        use_brand_kit: useBrand,
      });
      toast.success("Draft created");
      onCreated?.(d);
      setOpen(false);
      setTitle(defaultTitle); setPrompt(defaultPrompt);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Compose failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        data-testid="ai-studio-composer-open-btn"
        variant={triggerVariant}
        onClick={() => setOpen(true)}
      >
        {triggerLabel}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="ai-studio-composer" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Compose with AI Studio</DialogTitle>
            <DialogDescription>
              AI drafts, you authorise. Approval here is an internal state
              change — no customer email is sent and nothing is published.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input
                data-testid="ai-studio-composer-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div>
              <Label>Kind</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="ai-studio-composer-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Text</SelectItem>
                  <SelectItem value="photo">Photo (prompt only)</SelectItem>
                  <SelectItem value="video">Video (prompt only)</SelectItem>
                </SelectContent>
              </Select>
              {kind !== "text" && (
                <p className="text-[11px] text-neutral-500 mt-1">
                  Phase 6C v1 records the authorized {kind} prompt. Use the
                  existing AI Studio {kind} tab to render binary output.
                </p>
              )}
            </div>
            <div>
              <Label>Prompt</Label>
              <Textarea
                data-testid="ai-studio-composer-prompt"
                rows={8}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                data-testid="ai-studio-composer-brand-toggle"
                checked={useBrand}
                onCheckedChange={setUseBrand}
              />
              <Label className="cursor-pointer">
                Apply Brand Kit (snapshot stored with this version)
              </Label>
            </div>
            {sourceEntityType !== "manual" && (
              <div className="text-[11px] rounded-md bg-blue-50 border border-blue-200 text-blue-900 p-2">
                Source: <strong>{sourceEntityType}</strong>
                {sourceEntityId ? ` · ${sourceEntityId.slice(0, 10)}…` : ""}
                . The server re-fetches this record and verifies your
                authorization before composing.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid="ai-studio-composer-submit-btn"
              disabled={busy} onClick={submit}
            >
              {busy ? "Composing…" : "Compose draft"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
